import React, { useMemo, useState } from "react";
import { Check, ChevronDown, CircleDashed } from "lucide-react";
import { cn } from "@/lib/utils";
import { STATUS_LABEL } from "./tree";

/** Pre-order list of the rows under one branch – groups the person folded are skipped. */
function flatten(branch, closed) {
  const out = [];
  const walk = (n, depth) => { out.push({ n, depth }); if (n.children.length && !closed.has(n.id)) n.children.forEach((c) => walk(c, depth + 1)); };
  branch.children.forEach((c) => walk(c, 1));
  return out;
}

/**
 * The same tree as a readable list for phones: one section per branch, groups as headings, choices as rows with their
 * status in words. Nothing is shrunk; the data is the map's data.
 */
export function Steps({ marked, hasValve, onOpen, onChoose, dimOthers }) {
  const [closed, setClosed] = useState(() => new Set());
  const toggle = (id) => setClosed((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const sections = useMemo(() => marked.root.children.map((b) => ({ b, rows: flatten(b, closed) })), [marked, closed]);
  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-10 pt-14" data-testid="visuelt-steps">
      {sections.map(({ b, rows }) => (
        <section key={b.id} className="mb-4" data-testid={`visuelt-step-branch-${b.id}`}>
          <button type="button" onClick={() => onOpen(b)} className="flex w-full items-baseline gap-2 py-2 text-left"><span className="text-[16px] font-semibold tracking-[-0.01em]">{b.label}</span><span className="k-meta">{b.sub}{b.cell ? ` · ${b.cell}` : ""}</span></button>
          <ul className="divide-y divide-black/[0.05]">
            {rows.map(({ n, depth }) => {
              const group = n.children.length > 0;
              const dim = dimOthers && hasValve && !n.selected && n.status !== "avklar";
              return (
                <li key={n.id} className={cn("flex items-center gap-2 py-2", dim && "opacity-60")} style={{ paddingLeft: (depth - 1) * 16 }} data-testid={`visuelt-step-${n.id}`} data-status={n.status} data-depth={depth}>
                  {n.status === "valgt" ? <Check className="h-4 w-4 shrink-0 text-[hsl(var(--brand-strong))]" strokeWidth={2.5} /> : n.status === "avklar" ? <CircleDashed className="h-4 w-4 shrink-0 text-[hsl(var(--v-assumed))]" /> : <span className="h-4 w-4 shrink-0" />}
                  <button type="button" onClick={() => onOpen(n)} className="min-w-0 flex-1 text-left">
                    <span className={cn("block truncate text-[14px]", group ? "font-semibold" : "font-medium")}>{n.label}</span>
                    <span className="block truncate text-[12.5px] text-muted-foreground">{[n.part, n.cell, hasValve ? STATUS_LABEL[n.status] : n.rule === "ikke_valgbar" ? "ikke valgbar ennå" : null].filter(Boolean).join(" · ")}</span>
                  </button>
                  {hasValve && n.canChoose && !group ? <button type="button" onClick={() => onChoose(n)} className="shrink-0 rounded-full bg-[hsl(var(--brand))] px-2.5 py-1 text-[12px] font-semibold text-white" data-testid={`visuelt-step-choose-${n.id}`}>Velg</button> : null}
                  {group ? <button type="button" onClick={() => toggle(n.id)} aria-label={closed.has(n.id) ? "Vis" : "Skjul"} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-black/[0.05]" data-testid={`visuelt-step-toggle-${n.id}`}><ChevronDown className={cn("h-4 w-4 transition-transform", closed.has(n.id) && "-rotate-90")} /></button> : null}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
